import photoboothSalon from "@/assets/photobooth-salon-mariage.jpg.asset.json";
import karaokeHero from "@/assets/location-pack-karaoke-soiree-anniversaire-vaucluse.webp.asset.json";
import karaokeGroupe from "@/assets/pack-karaoke-micro-chant-groupe-amis-avignon.webp.asset.json";
import karaokeSoiree from "@/assets/location-karaoke-cavaillon-soiree-privee.webp.asset.json";

export type Equipment = {
  slug: string;
  name: string;
  category: "Sonorisation" | "Éclairage" | "Effets spéciaux" | "Vidéo" | "Animation";
  short: string;
  text: string;
  longText: string;
  image: string;
  specs: { label: string; value: string }[];
  useCases: string[];
  priceFrom: string;
  /** Grille tarifaire détaillée (optionnelle) affichée sur la fiche produit. */
  pricingTiers?: { label: string; price: string; detail: string }[];
  /** Galerie photo optionnelle (URL + alt SEO) affichée sur la fiche produit. */
  gallery?: { src: string; alt: string }[];
  faq: { q: string; a: string }[];
};

export const EQUIPMENT: Equipment[] = [
  {
    slug: "console-pioneer",
    name: "Console DJ Pioneer CDJ-3000",
    category: "Sonorisation",
    short: "Régie DJ standard mondial",
    text: "Platines Pioneer CDJ-3000 + mixeur DJM-900NXS2. Le standard des clubs et festivals du monde entier.",
    longText:
      "La régie Pioneer CDJ-3000 est le matériel de référence utilisé dans tous les plus grands clubs de la planète. Écran tactile 9 pouces haute résolution, 8 hot cues, traitement audio 96 kHz/32 bits. Couplée au mixeur DJM-900NXS2 quatre voies avec effets intégrés (filter, echo, reverb), elle offre une fiabilité et une qualité sonore irréprochables. Disponible en location à Cavaillon, Avignon, dans tout le Vaucluse et la région PACA.",
    image: "/__l5e/assets-v1/ccb770c5-04e8-4230-82c9-8a3332ae58a9/materiel-pro-setup.webp",
    specs: [
      { label: "Platines", value: "2 × Pioneer CDJ-3000" },
      { label: "Mixeur", value: "Pioneer DJM-900NXS2" },
      { label: "Connectique", value: "USB, Link, RCA, XLR" },
      { label: "Flight-case", value: "Inclus" },
    ],
    useCases: ["Soirées privées", "Mariages haut de gamme", "Clubs & bars", "Événements corporate"],
    priceFrom: "350 €/jour",
    faq: [
      { q: "La livraison et l'installation sont-elles incluses ?", a: "Sur option. Nous livrons et installons la régie à Cavaillon, Avignon et dans tout le Vaucluse, avec mise en route et contrôle." },
      { q: "Faut-il une caution ?", a: "Oui, une caution est demandée à la signature, restituée intégralement au retour du matériel en bon état." },
    ],
  },
  {
    slug: "sono-line-array",
    name: "Sonorisation Line Array — jusqu'à 2000 personnes",
    category: "Sonorisation",
    short: "Système pro grands événements",
    text: "Line-array dB Technologies / RCF, subwoofers, processeurs DSP, amplification numérique. Pour vos événements jusqu'à 2000 personnes.",
    longText:
      "Système de diffusion Line Array professionnel adapté aux moyens et grands événements. Têtes line-array dB Technologies VIO L208 ou RCF HDL, subwoofers SUB 9007 ou SUB 8004, processeurs DSP pour calage acoustique précis. Idéal pour mariages en château, soirées d'entreprise, concerts en plein air. Étude acoustique et calage offerts.",
    image: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=1000&q=70&fm=webp&auto=compress&fit=crop",
    specs: [
      { label: "Puissance", value: "6000 à 12000 W RMS" },
      { label: "Capacité", value: "300 à 2000 personnes" },
      { label: "Subwoofers", value: "Subs 18\" dB Technologies" },
      { label: "Calage DSP", value: "Inclus, technicien sur place" },
    ],
    useCases: ["Mariage en château", "Concert en plein air", "Festival", "Conférence grande salle"],
    priceFrom: "Sur devis",
    faq: [
      { q: "Un technicien son est-il présent pendant l'événement ?", a: "Oui, pour les configurations Line Array, un technicien est systématiquement présent pour assurer le calage et la régie." },
      { q: "Quelle alimentation électrique faut-il prévoir ?", a: "Selon la puissance déployée, prévoir une ligne 32A triphasée. Nous étudions cela avec vous en amont." },
    ],
  },
  {
    slug: "micros-hf",
    name: "Micros HF Shure & Sennheiser",
    category: "Sonorisation",
    short: "Discours, cérémonies laïques, karaoké",
    text: "Micros HF professionnels Shure ULX-D et Sennheiser EW. Bande de fréquence légale, autonomie longue, qualité broadcast.",
    longText:
      "Micros HF professionnels en bande UHF légale (470-694 MHz). Shure ULX-D ou Sennheiser EW G4 selon disponibilités. Parfaits pour les discours de mariage, cérémonies laïques en extérieur, conférences, karaoké, animation. Capsules Beta 58 ou SM58. Disponibles en main, serre-tête (headset) ou cravate (lavalier).",
    image: "https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=1000&q=70&fm=webp&auto=compress&fit=crop",
    specs: [
      { label: "Modèles", value: "Shure ULX-D / Sennheiser EW" },
      { label: "Capsules", value: "Main, serre-tête, cravate" },
      { label: "Autonomie", value: "8 à 10 h" },
      { label: "Bande", value: "UHF légale 470-694 MHz" },
    ],
    useCases: ["Cérémonie laïque", "Discours de mariage", "Conférence", "Karaoké"],
    priceFrom: "60 €/jour les 2 micros",
    faq: [
      { q: "Combien de micros HF peut-on louer en simultané ?", a: "Jusqu'à 8 micros HF synchronisés sans interférences, fréquences calées en amont." },
      { q: "Faut-il prévoir des piles ?", a: "Non, les accus sont fournis chargés avec leur station de charge." },
    ],
  },
  {
    slug: "enceintes-actives",
    name: "Location Enceintes Actives — RCF & Electro-Voice",
    category: "Sonorisation",
    short: "Enceintes amplifiées pro à louer",
    text: "Enceintes actives RCF ART 745-A et Electro-Voice EKX-15P, 1500 W chacune. Location à Avignon, Cavaillon et tout le Vaucluse.",
    longText:
      "Location d'enceintes actives professionnelles : RCF ART 745-A (1400 W, 15\") et Electro-Voice EKX-15P (1500 W, 15\"). Idéales pour sonoriser une soirée jusqu'à 150 personnes, un vin d'honneur, une conférence ou un événement en extérieur. Livraison possible à Avignon, Cavaillon, Aix, Nîmes et dans tout le Vaucluse. Câblage XLR fourni, pieds de perche inclus.",
    image: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=1000&q=70&fm=webp&auto=compress&fit=crop",
    specs: [
      { label: "Modèles", value: "RCF ART 745-A / EV EKX-15P" },
      { label: "Puissance", value: "1400 à 1500 W par enceinte" },
      { label: "Capacité", value: "80 à 150 personnes" },
      { label: "Inclus", value: "Pieds, câbles XLR, alimentation" },
    ],
    useCases: ["Vin d'honneur", "Soirée privée", "Conférence", "Cérémonie extérieure", "Séminaire d'entreprise", "Événement associatif"],
    priceFrom: "80 €/jour la paire",
    faq: [
      { q: "Peut-on louer une seule enceinte ?", a: "Oui, tarif à partir de 45 €/jour pour une seule enceinte active RCF ou EV, câble et pied inclus." },
      { q: "Livraison à Avignon possible ?", a: "Oui, livraison offerte dans un rayon de 30 km autour de Cavaillon (Avignon, Cavaillon, L'Isle-sur-la-Sorgue). Forfait 0,60 €/km au-delà." },
      { q: "Faut-il un caisson de basses en plus ?", a: "Pour une soirée dansante de plus de 60 personnes, oui : nous proposons un subwoofer RCF SUB 705-AS II à 45 €/jour en complément." },
      { q: "Peut-on brancher une console DJ dessus ?", a: "Absolument : entrées XLR et jack 6,35 mm. Nous fournissons le câblage adapté à votre source (DJ, PC, table de mixage)." },
      { q: "Combien de temps d'installation ?", a: "10 minutes pour une paire d'enceintes sur pieds. Nous pouvons aussi assurer la pose sur site pour 60 € forfaitaire." },
    ],
  },
  {
    slug: "lyres-led",
    name: "Lyres Beam & Spot LED",
    category: "Éclairage",
    short: "Faisceaux dynamiques et gobos",
    text: "Lyres beam 230W et lyres spot 200W LED, rotation totale, gobos, prisme, dimmer électronique.",
    longText:
      "Parc de lyres motorisées beam (faisceaux serrés type laser) et spot (avec gobos et iris) en LED 200-230W. Mouvement Pan/Tilt rapide, dimmer 0-100% sans scintillement, programmation DMX intégrée à la régie lumière. Indispensables pour créer une ambiance club ou un show structuré sur la piste de danse.",
    image: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1000&q=70&fm=webp&auto=compress&fit=crop",
    specs: [
      { label: "Puissance", value: "200 à 230 W LED" },
      { label: "Pan / Tilt", value: "540° / 270°" },
      { label: "Gobos", value: "8 + 1 ouvert" },
      { label: "Contrôle", value: "DMX 512 / Art-Net" },
    ],
    useCases: ["Soirée dansante", "Mariage avec piste club", "Anniversaire", "Bal d'entreprise"],
    priceFrom: "60 €/jour la lyre",
    faq: [
      { q: "Faut-il un technicien lumière ?", a: "Non, les lyres peuvent être programmées en mode standalone ou pilotées par le DJ Val depuis sa régie lumière." },
      { q: "Y a-t-il un risque pour les yeux ?", a: "Non, les LED sont conformes EN 62471 et utilisées en faisceau dirigé vers la piste, jamais directement vers le public." },
    ],
  },
  {
    slug: "par-led",
    name: "Projecteurs PAR LED RGBW",
    category: "Éclairage",
    short: "Mise en lumière architecturale",
    text: "Pars LED RGBW 18×12W, sur batterie ou secteur, sans-fil DMX, parfaits pour mettre en lumière une salle ou un château.",
    longText:
      "Pars LED RGBW haute puissance (18 LEDs × 12W) avec batterie intégrée et DMX sans-fil. Mise en lumière de salles de réception, façades de château, arbres, structures. Mémorisation des teintes, gradation fluide, étanchéité IP54 pour usage extérieur protégé. Pose en location simple ou avec installation.",
    image: "https://images.unsplash.com/photo-1530023367847-a683933f4172?w=1000&q=70&fm=webp&auto=compress&fit=crop",
    specs: [
      { label: "LEDs", value: "18 × 12W RGBW" },
      { label: "Autonomie batterie", value: "10 h en couleur fixe" },
      { label: "DMX", value: "Filaire ou sans-fil" },
      { label: "Étanchéité", value: "IP54 (extérieur protégé)" },
    ],
    useCases: ["Décoration salle", "Façade château", "Cocktail extérieur", "Mariage féerique"],
    priceFrom: "30 €/jour le projecteur",
    faq: [
      { q: "Peut-on choisir précisément les couleurs ?", a: "Oui, RGBW + ambré permet de reproduire toute teinte, y compris des blancs chauds très naturels." },
      { q: "Convient-il en extérieur ?", a: "En IP54, oui sous abri / pluie légère. Pour exposition directe, prévoir housse de protection." },
    ],
  },
  {
    slug: "lumiere-noire-uv",
    name: "Barres Lumière Noire UV",
    category: "Éclairage",
    short: "Effet fluo immédiat",
    text: "Barres LED UV haute puissance pour soirées fluo, halloween, ambiance club. Tout ce qui est blanc ou fluorescent ressort.",
    longText:
      "Barres LED UV professionnelles 18×3W. Lumière noire haute puissance, idéale pour soirées fluo (kit maquillage et accessoires en option), halloween, ambiance club rétro. Effet immédiat : tout ce qui est blanc, fluorescent ou phosphorescent ressort de manière spectaculaire.",
    image: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1000&q=70&fm=webp&auto=compress&fit=crop",
    specs: [
      { label: "LEDs", value: "18 × 3W UV 365nm" },
      { label: "Angle", value: "60°" },
      { label: "Refroidissement", value: "Passif silencieux" },
      { label: "Quantité conseillée", value: "1 barre / 30 m²" },
    ],
    useCases: ["Soirée fluo", "Halloween", "Anniversaire ado", "Soirée étudiante"],
    priceFrom: "40 €/jour la barre",
    faq: [
      { q: "Est-ce dangereux pour la peau ?", a: "Non, ce sont des UV-A faible puissance, équivalents à ceux d'une lampe Wood." },
      { q: "Fournissez-vous le maquillage fluo ?", a: "Oui, kit maquillage et accessoires fluo disponibles en option." },
    ],
  },
  {
    slug: "laser-rgb",
    name: "Lasers RGB Show",
    category: "Éclairage",
    short: "Show laser couleur",
    text: "Lasers RGB classe 3B, animations vectorielles, synchronisation musicale, parfait pour drop EDM ou intro de mariée.",
    longText:
      "Lasers RGB show classe 3B, puissance 1 à 3 W. Animations vectorielles préprogrammées, synchronisation musicale (mode son-actif), interfaçable ILDA pour show personnalisé. Effet wow garanti sur les drops ou entrées de mariés. Installation par technicien obligatoire pour respect des normes ARCEP.",
    image: "/__l5e/assets-v1/64271958-ebb0-433a-96e7-9deacdccedc7/laser-rgb.webp",
    specs: [
      { label: "Puissance", value: "1 à 3 W RGB" },
      { label: "Classe", value: "3B (installation pro)" },
      { label: "Modes", value: "Auto / Son / DMX / ILDA" },
      { label: "Sécurité", value: "Déclaration ARCEP gérée" },
    ],
    useCases: ["Soirée EDM", "Entrée des mariés", "Drop & climax", "Show personnalisé"],
    priceFrom: "150 €/soirée avec installation",
    faq: [
      { q: "Faut-il une autorisation ?", a: "Pour les classes 3B/4, oui : nous gérons la déclaration ARCEP et l'installation aux normes." },
      { q: "Le laser peut-il viser le public ?", a: "Non, faisceau dirigé au-dessus du public, configuration sécurité contrôlée avant chaque show." },
    ],
  },
  {
    slug: "guirlandes-guinguette",
    name: "Guirlandes Guinguette",
    category: "Éclairage",
    short: "Décor lumineux intemporel",
    text: "Guirlandes ampoules à filaments E27, installation sur poteaux, arbres ou structures. Atmosphère chaleureuse instantanée.",
    longText:
      "Guirlandes guinguette ampoules à filaments E27 (LED basse consommation), 10 m par tronçon raccordables. Installation sur poteaux télescopiques, arbres, charpentes ou structures alu. Crée une atmosphère chaleureuse incomparable pour cocktails, dîners d'extérieur et dancefloors en plein air.",
    image: "https://images.unsplash.com/photo-1530023367847-a683933f4172?w=1000&q=70&fm=webp&auto=compress&fit=crop",
    specs: [
      { label: "Longueur", value: "10 m par tronçon" },
      { label: "Ampoules", value: "E27 filament LED 2W" },
      { label: "Espacement", value: "1 ampoule tous les 50 cm" },
      { label: "Étanchéité", value: "IP44 extérieur" },
    ],
    useCases: ["Cocktail extérieur", "Dîner en jardin", "Dancefloor en plein air", "Mariage champêtre"],
    priceFrom: "8 €/mètre installé",
    faq: [
      { q: "Pouvez-vous installer en hauteur ?", a: "Oui, jusqu'à 6 m avec poteaux télescopiques et haubans, sur sol meuble ou plat." },
      { q: "Et s'il pleut ?", a: "Les guirlandes sont IP44, étanches aux projections d'eau. Pour pluie battante, prévoir mise hors tension." },
    ],
  },
  {
    slug: "canon-co2",
    name: "Canon CO₂",
    category: "Effets spéciaux",
    short: "Jets de fumée glaciale",
    text: "Canons CO₂ professionnels, bouteilles fournies, déclenchement sur drops musicaux. Effet visuel et thermique spectaculaire.",
    longText:
      "Canons CO₂ professionnels avec bouteilles de CO₂ alimentaire fournies. Jet vertical jusqu'à 8 m, effet brouillard glacial spectaculaire et sensation de fraîcheur immédiate. Déclenchement manuel par DJ sur les drops musicaux ou sur les chorégraphies. Installation et sécurité gérées par technicien.",
    image: "/__l5e/assets-v1/dcf79672-aabe-45d0-9d20-ef4bf55d1ae5/soiree-co2.webp",
    specs: [
      { label: "Jet", value: "Vertical jusqu'à 8 m" },
      { label: "CO₂", value: "Bouteilles 10 kg fournies" },
      { label: "Déclenchement", value: "Manuel DJ ou DMX" },
      { label: "Autonomie", value: "20 à 30 shots par bouteille" },
    ],
    useCases: ["Drop EDM", "Entrée mariés", "Climax show", "Anniversaire spectaculaire"],
    priceFrom: "180 €/soirée le canon (bouteille incluse)",
    faq: [
      { q: "Est-ce sans danger ?", a: "Oui, CO₂ alimentaire à -78°C en jet vertical, hors d'atteinte du public. Sécurité totale." },
      { q: "Combien de bouteilles prévoir ?", a: "Une bouteille = 20 à 30 shots. Pour une soirée animée, prévoir 2 bouteilles par canon." },
    ],
  },
  {
    slug: "machine-confettis",
    name: "Canon à Confettis",
    category: "Effets spéciaux",
    short: "Pluie de couleurs",
    text: "Canons à confettis biodégradables, plusieurs coloris, déclenchement timing précis. L'effet wow garanti.",
    longText:
      "Canons à confettis Magic FX électriques. Confettis papier soie biodégradables, plusieurs coloris au choix (blanc, or, rose, multicolore). Déclenchement manuel ou DMX synchronisé au mix. Effet spectaculaire et photogénique pour la première danse, l'entrée des mariés ou un drop musical.",
    image: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1000&q=70&fm=webp&auto=compress&fit=crop",
    specs: [
      { label: "Confettis", value: "Papier soie biodégradable" },
      { label: "Couleurs", value: "Blanc, or, rose, multi" },
      { label: "Portée", value: "8 à 10 m" },
      { label: "Recharges", value: "Vendues à l'unité" },
    ],
    useCases: ["Première danse", "Entrée mariés", "Drop musical", "Photo finale"],
    priceFrom: "120 €/soirée la paire",
    faq: [
      { q: "Le nettoyage est-il à votre charge ?", a: "Le nettoyage est à la charge du lieu. Confettis 100% papier biodégradable, sans micro-plastique." },
      { q: "Combien de tirs ?", a: "Une recharge = 1 tir, prévoir 2 à 4 recharges par canon selon l'effet souhaité." },
    ],
  },
  {
    slug: "machine-fumee-lourde",
    name: "Machine à Fumée Lourde (Low Fog)",
    category: "Effets spéciaux",
    short: "Effet brouillard au sol",
    text: "Machine à fumée lourde au sol type 'cloud nine'. Effet magique pour la première danse, les mariés semblent danser sur les nuages.",
    longText:
      "Machine à fumée lourde Antari ICE-101, fluide ICE-101 et glace pilée. Produit un brouillard dense, lourd, qui reste au sol et donne l'impression que les mariés dansent sur un nuage. Réservé à la première danse (autonomie 5 min de production). Effet inoubliable, photo et vidéo magiques garanties.",
    image: "/__l5e/assets-v1/b8f8cfd0-8e17-486f-a036-2ddceff7f93a/fumee-lourde.webp",
    specs: [
      { label: "Modèle", value: "Antari ICE-101 ou équivalent" },
      { label: "Hauteur fumée", value: "30 à 50 cm au sol" },
      { label: "Autonomie", value: "5 à 7 min de production" },
      { label: "Fluide", value: "Eau distillée + glace pilée" },
    ],
    useCases: ["Première danse", "Cérémonie laïque", "Ouverture de bal", "Show artistique"],
    priceFrom: "180 €/soirée",
    faq: [
      { q: "Y a-t-il un risque de glisser ?", a: "Non, le brouillard est composé d'eau pure qui s'évapore. Sol non-glissant." },
      { q: "Cela déclenche-t-il les détecteurs incendie ?", a: "Non, fumée à base d'eau pure, pas de particules détectées. Toujours vérifier avec le lieu en amont." },
    ],
  },
  {
    slug: "machine-bulles",
    name: "Machine à Bulles XXL",
    category: "Effets spéciaux",
    short: "Atmosphère féerique",
    text: "Machine à bulles XXL, production massive de bulles. Parfait pour mariages, baptêmes, vins d'honneur et soirées enfants.",
    longText:
      "Machine à bulles haute production professionnelle. Génère un nuage de bulles continu pour vin d'honneur, cérémonie laïque, soirée enfants, première danse féerique. Fluide à bulles non-tachant, sans danger. Effet poétique et photogénique.",
    image: "https://images.unsplash.com/photo-1530023367847-a683933f4172?w=1000&q=70&fm=webp&auto=compress&fit=crop",
    specs: [
      { label: "Production", value: "300+ bulles/min" },
      { label: "Réservoir", value: "1 L (autonomie 1 h)" },
      { label: "Fluide", value: "Non-tachant, sans danger" },
      { label: "Contrôle", value: "Auto ou télécommande" },
    ],
    useCases: ["Vin d'honneur", "Cérémonie laïque", "Baptême", "Soirée enfants"],
    priceFrom: "60 €/soirée",
    faq: [
      { q: "Cela tache-t-il les vêtements ?", a: "Non, fluide à bulles certifié non-tachant, sans danger pour la peau et les textiles." },
      { q: "Convient-il aux enfants ?", a: "Parfaitement, fluide non-toxique, c'est un grand classique des baptêmes et anniversaires enfants." },
    ],
  },
  {
    slug: "machine-mousse",
    name: "Machine à Mousse",
    category: "Effets spéciaux",
    short: "Soirée plein air d'été",
    text: "Canon à mousse hypoallergénique et biodégradable. Idéal campings, villas, soirées étudiantes d'été.",
    longText:
      "Canon à mousse Big Foam 3000W. Produit une mousse dense, hypoallergénique, biodégradable, sans danger pour la peau ou les yeux. Réservé aux soirées en plein air (terrasse, piscine, jardin). Effet 'foam party' instantané, succès garanti l'été.",
    image: "/__l5e/assets-v1/fcc60913-f7d3-4a98-890b-ca97a0e34ab1/soiree-mousse.webp",
    specs: [
      { label: "Puissance", value: "3000 W" },
      { label: "Portée mousse", value: "Jusqu'à 8 m" },
      { label: "Concentré", value: "Hypoallergénique biodégradable" },
      { label: "Alimentation eau", value: "Tuyau jardin (fourni)" },
    ],
    useCases: ["Soirée piscine", "Camping & villa", "Anniversaire ado", "Soirée étudiante"],
    priceFrom: "220 €/soirée concentré inclus",
    faq: [
      { q: "Est-ce dangereux pour la piscine ?", a: "Non, mousse biodégradable qui se dissout. Filtrer la piscine après usage." },
      { q: "Peut-on le faire à l'intérieur ?", a: "Déconseillé : sol glissant et nettoyage lourd. À réserver à l'extérieur." },
    ],
  },
  {
    slug: "videoprojecteur",
    name: "Vidéoprojecteur + Écran",
    category: "Vidéo",
    short: "Diaporama, clips, conférences",
    text: "Vidéoprojecteur HD 5000 lumens, écran motorisé jusqu'à 4 m. Diffusion clips, diaporamas, conférences.",
    longText:
      "Vidéoprojecteur HD 5000 lumens (visible même en lumière ambiante), écran motorisé jusqu'à 4 m de base. Diffusion de clips musicaux, diaporamas de mariage, supports de conférence, retransmissions sportives. Connectique HDMI, VGA, USB. Installation et test offerts.",
    image: "https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=1000&q=70&fm=webp&auto=compress&fit=crop",
    specs: [
      { label: "Luminosité", value: "5000 lumens" },
      { label: "Résolution", value: "Full HD 1080p" },
      { label: "Écran", value: "Motorisé 3 ou 4 m base" },
      { label: "Connectique", value: "HDMI, VGA, USB" },
    ],
    useCases: ["Diaporama mariage", "Clips musicaux", "Conférence", "Retransmission sportive"],
    priceFrom: "150 €/jour avec écran",
    faq: [
      { q: "Peut-on diffuser en plein jour ?", a: "À 5000 lumens, oui en intérieur lumineux. Pour plein soleil extérieur, écran LED recommandé." },
      { q: "Fournissez-vous le contenu vidéo ?", a: "Nous diffusons votre contenu (clé USB, ordinateur). Sur demande, conseil pour le montage diaporama." },
    ],
  },
  {
    slug: "photobooth",
    name: "Photobooth Premium",
    category: "Animation",
    short: "Fiche technique — à partir de 190 €",
    text: "Borne photobooth à écran tactile, photos numériques illimitées dès 190 € la soirée, forfait illimité avec tirages 10×15 à 390 €.",
    longText:
      "Fiche technique de la borne photobooth DJ Val : écran tactile, éclairage annulaire LED et imprimante dye-sub qualité labo pour des tirages 10×15 cm. Deux formules : la formule digitale à 190 € la soirée, avec photos numériques illimitées et retrait à l'atelier de Cavaillon ; le forfait illimité à 390 € la soirée, tirages 10×15 illimités compris, livraison, installation, réglages et démontage assurés par DJ Val. Fond toile personnalisable à vos couleurs ou prénoms, bac d'accessoires fourni (chapeaux, lunettes, perruques, pancartes) et galerie numérique privée partagée après la soirée en option.",
    image: photoboothSalon.url,
    specs: [
      { label: "Éclairage", value: "Ring light LED intégré" },
      { label: "Écran", value: "Tactile, interface guidée" },
      { label: "Impression", value: "Dye-sub 10×15 cm (forfait 390 €)" },
      { label: "Fond toile", value: "Personnalisable" },
      { label: "Accessoires", value: "Bac complet fourni" },
      { label: "Alimentation", value: "220 V, prise standard" },
    ],
    useCases: ["Mariage", "Anniversaire", "Soirée entreprise", "Bar-Mitsva"],
    priceFrom: "190 €",
    pricingTiers: [
      {
        label: "Formule digitale",
        price: "190 €",
        detail: "La soirée, photos numériques illimitées comprises. Retrait et retour à l'atelier de Cavaillon. Livraison possible en option.",
      },
      {
        label: "Forfait illimité",
        price: "390 €",
        detail: "La soirée, tirages 10×15 illimités compris, livraison, installation, réglages et démontage par DJ Val.",
      },
    ],
    faq: [
      { q: "Quel est le tarif de la borne photobooth ?", a: "À partir de 190 € la soirée en formule digitale (photos numériques illimitées, retrait à Cavaillon), et 390 € en forfait illimité avec tirages 10×15 illimités, livraison et installation comprises." },
      { q: "Les tirages papier sont-ils inclus ?", a: "Les tirages 10×15 illimités sont compris dans le forfait à 390 €. La formule à 190 € donne des photos numériques illimitées, sans impression." },
      { q: "Que comprend la location ?", a: "La borne à écran tactile, l'imprimante dye-sub (forfait 390 €), le bac d'accessoires, la galerie numérique privée en option, et l'installation, les réglages et le démontage par DJ Val sur le forfait illimité." },
      { q: "Peut-on personnaliser le design des photos ?", a: "Oui, gabarit aux prénoms, date, logo ou couleurs au choix, validé en amont." },
    ],

  },
  {
    slug: "karaoke",
    name: "Pack Karaoké Complet",
    category: "Animation",
    short: "Pack karaoké complet à partir de 200 €",
    text: "Pack karaoké complet à louer : système son, vidéoprojecteur, écran de projection, 2 micros et les identifiants d'un logiciel de karaoké en ligne (ordinateur non fourni).",
    longText:
      "Le pack karaoké de DJ Val réunit tout ce qu'il faut pour transformer une salle, un mas ou une terrasse en scène le temps d'une soirée : un système son, un vidéoprojecteur, un écran de projection, deux micros et les identifiants d'accès à un logiciel de karaoké en ligne avec son catalogue de titres. Le pack est prêt à brancher, pré-réglé avant le départ de l'atelier, et se monte en quelques minutes. Seul élément à prévoir de votre côté : un ordinateur portable, sur lequel vous vous connectez au logiciel avec les identifiants fournis — les paroles s'affichent alors sur l'écran de projection et le son passe par le système fourni. La livraison, l'installation sur place et l'animation par DJ Val sont des options qui s'ajoutent au tarif du pack, jamais incluses d'office : vous ne payez que ce dont vous avez réellement besoin.",
    image: karaokeHero.url,
    gallery: [
      { src: karaokeGroupe.url, alt: "Groupe d'amis chantant au micro avec le pack karaoké loué à Avignon" },
      { src: karaokeSoiree.url, alt: "Soirée privée karaoké à Cavaillon avec micro et système son loués" },
    ],
    specs: [
      { label: "Système son", value: "Sonorisation complète + câblage" },
      { label: "Vidéoprojecteur", value: "1 vidéoprojecteur inclus" },
      { label: "Écran", value: "1 écran de projection sur pied" },
      { label: "Micros", value: "2 micros inclus" },
      { label: "Logiciel", value: "Identifiants karaoké en ligne fournis" },
      { label: "À prévoir", value: "Votre ordinateur portable" },
      { label: "Options", value: "Livraison, installation, animation" },
    ],
    useCases: ["Anniversaire", "EVJF / EVG", "Mariage", "Soirée entreprise", "Fête de famille", "Bar associatif"],
    priceFrom: "200 €",
    pricingTiers: [
      {
        label: "Pack karaoké complet",
        price: "200 €",
        detail: "Système son, vidéoprojecteur, écran de projection, 2 micros et identifiants du logiciel de karaoké en ligne. Ordinateur non fourni. Retrait à l'atelier de Cavaillon.",
      },
      {
        label: "Option livraison + installation",
        price: "sur devis",
        detail: "Je livre le pack sur votre lieu de réception, je monte l'écran, je câble le son et le vidéoprojecteur, je teste avec vous puis je récupère tout le lendemain. Tarif selon la distance depuis Cavaillon.",
      },
      {
        label: "Option animation par DJ Val",
        price: "sur devis",
        detail: "Je reste sur place : lancement des titres, gestion de la file d'attente, relances micro entre deux chansons et bascule en soirée dansante à la fin du karaoké.",
      },
    ],
    faq: [
      { q: "Que contient exactement le pack karaoké ?", a: "Un système son, un vidéoprojecteur, un écran de projection, deux micros et les identifiants d'accès à un logiciel de karaoké en ligne. L'ordinateur portable n'est pas fourni : vous utilisez le vôtre pour vous connecter au logiciel." },
      { q: "Combien coûte la location du pack karaoké ?", a: "200 € pour le pack complet. La livraison, l'installation sur place et l'animation par DJ Val sont des options facturées en plus, sur devis selon votre lieu et votre besoin. TVA non applicable, art. 293 B du CGI." },
      { q: "La livraison et l'installation sont-elles comprises dans les 200 € ?", a: "Non. Le tarif de 200 € correspond au pack seul, à retirer à Cavaillon. La livraison et l'installation sont proposées en option et chiffrées selon la distance et la configuration de la salle." },
      { q: "Dois-je fournir un ordinateur ?", a: "Oui. Le logiciel de karaoké fonctionne en ligne : vous vous connectez depuis votre propre ordinateur portable avec les identifiants que je vous transmets. Prévoyez aussi une connexion internet ou un partage de connexion mobile." },
      { q: "Faut-il des connaissances techniques ?", a: "Non. Tout arrive pré-réglé, avec un schéma de branchement simple. Je prends le temps de vous expliquer le montage au retrait, et une ligne d'assistance téléphonique reste ouverte pendant la soirée." },
      { q: "Le karaoké fonctionne-t-il en extérieur ?", a: "Oui, sous réserve d'une alimentation 220 V et d'un abri en cas d'humidité. En extérieur, le vidéoprojecteur ne devient vraiment lisible qu'une fois la nuit tombée." },
      { q: "Peut-on louer le pack pour un week-end entier ?", a: "Oui, tarif dégressif : le second jour est facturé 50 % du premier. Restitution le lundi matin sans supplément." },
      { q: "Y a-t-il une caution ?", a: "Oui, une caution est demandée à la signature du contrat de location et restituée intégralement au retour du matériel en bon état." },
    ],
  },

];

export const EQUIPMENT_SLUGS = EQUIPMENT.map((e) => e.slug);
export const EQUIPMENT_CATEGORIES = ["Sonorisation", "Éclairage", "Effets spéciaux", "Vidéo", "Animation"] as const;
